import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  /** Initial session restoration finished (independent of role check). */
  sessionReady: boolean;
  /** Admin role check finished for the current session (or no session). */
  adminReady: boolean;
  /** Convenience: true when both session restore AND role check are done. */
  authReady: boolean;
  /** Legacy alias kept for compatibility: equivalent to !authReady. */
  loading: boolean;
  /** MFA state finished loading for the current session. */
  mfaReady: boolean;
  /** User is signed in but has no verified TOTP factor yet. */
  needsMfaSetup: boolean;
  /** User has a TOTP factor but session is still aal1 — needs to enter code. */
  needsMfaChallenge: boolean;
  /** Current session reached aal2 (or user is not signed in). */
  mfaVerified: boolean;
  /** First verified TOTP factor id, if any. */
  totpFactorId: string | null;
  /** Force a refresh of MFA state (call after enroll/verify/unenroll). */
  refreshMfa: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [adminReady, setAdminReady] = useState(false);
  const [mfaReady, setMfaReady] = useState(false);
  const [needsMfaSetup, setNeedsMfaSetup] = useState(false);
  const [needsMfaChallenge, setNeedsMfaChallenge] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(true);
  const [totpFactorId, setTotpFactorId] = useState<string | null>(null);

  /**
   * Verifies whether the given user has the `admin` role. Runs detached from
   * the auth-state callback so we never `await` inside Supabase's internal
   * lock (which can deadlock the auth-js client). Always resolves
   * `adminReady` to true at the end, even on errors, so the UI can move on.
   */
  const runAdminCheck = (userId: string) => {
    setAdminReady(false);
    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.error("[useAuth] Falha ao verificar role admin:", error.message);
          setIsAdmin(false);
        } else {
          setIsAdmin(!!data);
        }
        setAdminReady(true);
      });
  };

  /**
   * Computes MFA state for the current session:
   *  - no verified TOTP factor      → needsMfaSetup
   *  - has factor, currentLevel aal1 → needsMfaChallenge
   *  - currentLevel aal2             → mfaVerified
   * Always resolves mfaReady=true at the end so the gate can render.
   */
  const runMfaCheck = async () => {
    setMfaReady(false);
    try {
      const [{ data: factorsData, error: factorsErr }, { data: aalData, error: aalErr }] = await Promise.all([
        supabase.auth.mfa.listFactors(),
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      ]);
      if (factorsErr) console.warn("[useAuth] listFactors:", factorsErr.message);
      if (aalErr) console.warn("[useAuth] getAAL:", aalErr.message);

      const verifiedTotp = (factorsData?.totp ?? []).filter((f) => f.status === "verified");
      const firstId = verifiedTotp[0]?.id ?? null;
      const currentLevel = aalData?.currentLevel ?? "aal1";

      setTotpFactorId(firstId);
      if (verifiedTotp.length === 0) {
        setNeedsMfaSetup(true);
        setNeedsMfaChallenge(false);
        setMfaVerified(false);
      } else if (currentLevel !== "aal2") {
        setNeedsMfaSetup(false);
        setNeedsMfaChallenge(true);
        setMfaVerified(false);
      } else {
        setNeedsMfaSetup(false);
        setNeedsMfaChallenge(false);
        setMfaVerified(true);
      }
    } catch (e) {
      console.error("[useAuth] runMfaCheck error:", (e as Error)?.message);
      // Fail open visually (don't lock the user out of the UI) but keep verified=false
      setNeedsMfaSetup(false);
      setNeedsMfaChallenge(false);
      setMfaVerified(false);
    } finally {
      setMfaReady(true);
    }
  };

  const resetMfa = () => {
    setNeedsMfaSetup(false);
    setNeedsMfaChallenge(false);
    setMfaVerified(true);
    setTotpFactorId(null);
    setMfaReady(true);
  };

  useEffect(() => {
    let cancelled = false;

    // 1) Register the listener FIRST and keep it 100% synchronous. Any async
    //    work (like the role check) MUST be deferred via setTimeout/promise
    //    chaining — never `await` inside this callback.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        if (cancelled) return;

        setSession(nextSession);
        setUser(nextSession?.user ?? null);
        setSessionReady(true);

        if (nextSession?.user) {
          // Defer to next tick so we don't run DB calls inside the auth lock.
          const userId = nextSession.user.id;
          setTimeout(() => {
            if (cancelled) return;
            runAdminCheck(userId);
            runMfaCheck();
          }, 0);
        } else {
          setIsAdmin(false);
          setAdminReady(true);
          resetMfa();
        }
      }
    );

    // 2) Then restore any existing session. The listener above will ALSO
    //    fire for the restored session in most flows, but we set state
    //    eagerly here so the first paint already has the correct values.
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      if (cancelled) return;

      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      setSessionReady(true);

      if (existingSession?.user) {
        runAdminCheck(existingSession.user.id);
        runMfaCheck();
      } else {
        setIsAdmin(false);
        setAdminReady(true);
        resetMfa();
      }
    }).catch((err) => {
      console.error("[useAuth] Falha ao restaurar sessão:", err);
      if (cancelled) return;
      setSessionReady(true);
      setAdminReady(true);
      resetMfa();
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    // Reset the admin gate so consumers wait for fresh verification rather
    // than reading a stale `isAdmin` from a previous session.
    setAdminReady(false);
    setMfaReady(false);

    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      // No session was created — release the gate so the UI can re-render.
      setAdminReady(true);
      setMfaReady(true);
      return { error: new Error(error.message) };
    }

    // Session/user/admin state will be populated by onAuthStateChange.
    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setAdminReady(true);
    resetMfa();
  };

  // Auth is "ready" only when BOTH the session has been restored AND the
  // admin role check has resolved. Consumers that only care about the
  // session can read `sessionReady`; consumers gating on admin status
  // should wait for `authReady` (or check `adminReady` explicitly).
  const authReady = sessionReady && adminReady;
  const loading = !authReady;

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isAdmin,
        sessionReady,
        adminReady,
        authReady,
        loading,
        mfaReady,
        needsMfaSetup,
        needsMfaChallenge,
        mfaVerified,
        totpFactorId,
        refreshMfa: runMfaCheck,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
